import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    fetchScanImages,
    fetchScanMeshes,
    type ScanImagesResult
} from '../components/accounts/practice/itero-browser/scan-preview/scanAssets';
import type { ScanImageAssetType } from '../components/accounts/practice/itero-browser/scan-preview/scanImageTabs';
import type { IteroAssetResult, IteroAssetType } from '../services/iteroBrowse';
import {
    INITIAL_DOWNLOAD_PHASE,
    type ScanDownloadPhase
} from '../components/accounts/practice/itero-browser/scan-preview/scanPreviewState';
import { BROWSE_QUERY_OPTIONS, type IteroBrowseScope } from './useIteroBrowse';
import { ITERO_SCAN_IMAGES_KEY, ITERO_SCAN_MESHES_KEY } from './queryKeys';

// Enabled once the order's Scans tab opens; the unzipped blobs live in memory only.
export function useIteroScanImages(
    scope: IteroBrowseScope,
    rxId: string,
    assetType: ScanImageAssetType,
    enabled: boolean
) {
    return useQuery({
        queryKey: ITERO_SCAN_IMAGES_KEY(scope.practiceId, scope.clinicId, rxId, assetType),
        queryFn: ({ signal }) => fetchScanImages(scope.practiceId, rxId, assetType, signal),
        enabled,
        ...BROWSE_QUERY_OPTIONS
    });
}

export type CachedAssetLookup = (assetType: IteroAssetType) => IteroAssetResult | null;

// Images already loaded for the gallery are saved from memory rather than downloaded again.
export function useCachedScanArchive(scope: IteroBrowseScope, rxId: string): CachedAssetLookup {
    const queryClient = useQueryClient();
    const { practiceId, clinicId } = scope;
    return useCallback(
        (assetType) => {
            if (assetType === 'IDE') return null;
            const cached = queryClient.getQueryData<ScanImagesResult>(
                ITERO_SCAN_IMAGES_KEY(practiceId, clinicId, rxId, assetType)
            );
            return cached?.kind === 'ready' ? { kind: 'ready', ...cached.archive } : null;
        },
        [queryClient, practiceId, clinicId, rxId]
    );
}

// gcTime 0: the 3D buffers run to tens of MB, so they are dropped as soon as the viewer closes.
export function useIteroScanMeshes(scope: IteroBrowseScope, rxId: string) {
    const [phase, setPhase] = useState<ScanDownloadPhase>(INITIAL_DOWNLOAD_PHASE);
    const query = useQuery({
        queryKey: ITERO_SCAN_MESHES_KEY(scope.practiceId, scope.clinicId, rxId),
        queryFn: ({ signal }) => {
            setPhase(INITIAL_DOWNLOAD_PHASE);
            return fetchScanMeshes(scope.practiceId, rxId, { signal, onPhase: setPhase });
        },
        ...BROWSE_QUERY_OPTIONS,
        staleTime: 0,
        gcTime: 0
    });
    return { query, phase };
}
